import React from 'react';
import { preload } from 'react-dom';
import { getImageProps } from 'next/image';
import { canOptimizeImage, getAbsoluteMediaUrl, toOptimizableSrc } from '@/core/utils/media';
import { ALL_BREAKPOINTS, breakpointsMedia, type Breakpoint } from '../breakpoints';

/**
 * How an image competes for bandwidth on first load:
 * - 'eager'    — the page's LCP candidate, visible on every breakpoint: fetched immediately at high priority.
 * - 'deferred' — an LCP candidate only on some breakpoints, typically inside a block CSS hides on the
 *                others (a desktop-only slider next to a mobile-only one). Eager would download it on
 *                every device, display:none or not; `loading="lazy"` keeps it off the wire where it's
 *                hidden, and a <link rel="preload" media> (see `preloadMedia`) still starts it at high
 *                priority from <head> where it is the LCP.
 * - 'none'     — an ordinary below-the-fold image.
 */
export type ImageLoadPriority = 'eager' | 'deferred' | 'none';

/**
 * Turns BlockRenderer's first-visible bookkeeping into an image priority: eager when the block is the
 * LCP candidate on every breakpoint, deferred (+ the media query to preload under) when only on some.
 */
export function lcpImagePriority(
  isFirstBlock: boolean | undefined,
  lcpBreakpoints: readonly Breakpoint[] | undefined,
): { priority: ImageLoadPriority; preloadMedia?: string } {
  if (!isFirstBlock) return { priority: 'none' };
  const breakpoints = lcpBreakpoints ?? ALL_BREAKPOINTS;
  if (breakpoints.length === 0) return { priority: 'none' };
  if (breakpoints.length === ALL_BREAKPOINTS.length) return { priority: 'eager' };
  return { priority: 'deferred', preloadMedia: breakpointsMedia(breakpoints) };
}

/** Must stay in sync with `images.qualities` in next.config.ts — the optimizer rejects anything else. */
export const IMAGE_QUALITY = { standard: 75, sharp: 88, high: 95 } as const;

interface ResolvedSource {
  src: string;
  srcSet?: string;
}

function resolveSource(raw: string, sizes: string, quality: number, unoptimized: boolean): ResolvedSource {
  const absolute = raw.startsWith('data:') ? raw : getAbsoluteMediaUrl(raw);
  // External URLs and data URIs aren't on the optimizer's allow-list (next.config.ts remotePatterns),
  // so they're served as-is. Animated GIF/WebP don't need special-casing: the optimizer detects them
  // and returns the original bytes.
  if (unoptimized || !canOptimizeImage(absolute)) return { src: absolute };
  const { props } = getImageProps({ src: toOptimizableSrc(absolute), alt: '', fill: true, sizes, quality });
  return { src: props.src, srcSet: props.srcSet };
}

interface ResponsiveImageProps {
  src: string;
  /** Art-directed variant for narrow screens; only one of the two is ever downloaded. */
  mobileSrc?: string;
  /** Last viewport width (px) that gets `mobileSrc` — must match the block's own CSS breakpoint. */
  mobileMaxWidth?: number;
  alt: string;
  sizes: string;
  quality?: number;
  unoptimized?: boolean;
  priority?: ImageLoadPriority;
  /** With priority 'deferred': the breakpoints (media query) on which this image is the LCP. */
  preloadMedia?: string;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Builder image with a resized srcset from the Next optimizer and, when a mobile variant exists,
 * a <picture> so the browser fetches only the one matching the viewport. Sizing and positioning
 * stay with the caller's className/style — this renders no layout of its own.
 */
export function ResponsiveImage({
  src,
  mobileSrc,
  mobileMaxWidth = 639,
  alt,
  sizes,
  quality = IMAGE_QUALITY.standard,
  unoptimized = false,
  priority = 'none',
  preloadMedia,
  className,
  style,
}: ResponsiveImageProps) {
  const desktop = resolveSource(src, sizes, quality, unoptimized);
  const mobile = mobileSrc && mobileSrc !== src ? resolveSource(mobileSrc, sizes, quality, unoptimized) : null;

  // React hoists this into <head> during SSR, so the preload scanner starts the request before the
  // lazy <img> is even laid out — but only where the media query matches. A <picture> picks its
  // source itself; preloading one of its sources here could fetch the wrong one.
  if (priority === 'deferred' && preloadMedia && !mobile && desktop.src) {
    preload(desktop.src, {
      as: 'image',
      imageSrcSet: desktop.srcSet,
      imageSizes: desktop.srcSet ? sizes : undefined,
      fetchPriority: 'high',
      media: preloadMedia,
    });
  }

  const img = (
    <img
      src={desktop.src}
      srcSet={desktop.srcSet}
      sizes={desktop.srcSet ? sizes : undefined}
      alt={alt}
      loading={priority === 'eager' ? 'eager' : 'lazy'}
      fetchPriority={priority === 'none' ? undefined : 'high'}
      decoding="async"
      className={className}
      style={style}
    />
  );

  if (!mobile) return img;

  return (
    <picture style={{ display: 'contents' }}>
      <source
        media={`(max-width: ${mobileMaxWidth}px)`}
        srcSet={mobile.srcSet ?? mobile.src}
        sizes={mobile.srcSet ? sizes : undefined}
      />
      {img}
    </picture>
  );
}
